/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useState } from "react";
import "../../pages/Trade/index.css";
import { Token } from "../../store/global/TokenEnum";
import { TokenInputSection } from "./sections/TokenInputSection";
import AmountInput from "./sections/AmountInput";
import { BalanceDetails } from "./sections/BalanceDetails";
import {
  MakerToken,
  SelectedToken,
  TakerToken,
  GlobalStore,
  WithAllocationPct,
} from "../../store/global/GlobalStore";
import { TokenSelectMode } from "../Modal/instance/SelectTokenModal/TokenSelectModal";
import { useStoreState } from "pullstate";
import { Slider } from "./sections/Slider";
import { PercentageLabel } from "./sections/PercentageLabel";
import { Amount } from "../../types/amount";
import { AmountLabel } from "./sections/AmountLabel";
import { RemoveButton } from "./sections/RemoveButton";
import { LockButton } from "./sections/LockButton";
import { Spinner } from "../Spinner/Spinner";
import { cloneDeep } from "lodash-es";
import { TradeRowWrapper } from "./TradeRowWrapper";
import { css } from "@emotion/react";
import { getTokenIcon } from "../../utils/tokenIconUtils";
import { getWrappedSymbolForChain } from "../../utils/chainUtils";
import { useBalanceForSymbol } from "../../hooks/useBalanceForSymbol";
import { getPriceForToken } from "../../utils/tokenMetadataUtils";
import { Quote } from "../../types/quote";
import { WrapQuote } from "../../types/wrapQuote";

export type CounterParty = "maker" | "taker";

export interface TradeLegProps {
  symbol: Token | undefined;
  tokenSelectMode: TokenSelectMode;
  counterParty: CounterParty;
}

export const TradeLeg: React.FC<TradeLegProps> = props => {
  const {
    quote,
    quoteError,
    wrapQuote,
    tokens,
    network,
    isSending,
    isMovingSlider,
    isRequestingQuote,
    theme,
  } = GlobalStore.useState(s => ({
    quote: s.quote,
    quoteError: s.quoteError,
    wrapQuote: s.wrapQuote,
    tokens: s.tokens,
    network: s.network,
    isSending: s.isSending,
    isMovingSlider: s.isMovingSlider,
    isRequestingQuote: s.isRequestingQuote,
    theme: s.theme,
  }));
  const price = getPriceForToken(props.symbol, tokens);
  const [isWaitingForNewQuote, setIsWaitingForNewQuote] = useState(false);
  const isSliderApplicable = props.counterParty === "maker" && props.tokenSelectMode === "multiple";
  const isAmountInputApplicable = props.counterParty === "taker";
  const counterPartyTokens: Array<MakerToken | TakerToken> = useStoreState(GlobalStore, s => {
    return props.counterParty === "maker" ? s.makerTokens : s.takerTokens;
  });
  const balance = useBalanceForSymbol(props.symbol);
  useEffect(() => {
    if (quote) {
      setIsWaitingForNewQuote(false);
    }
  }, [quote]);

  useEffect(() => {
    if (quote && isMovingSlider) {
      setIsWaitingForNewQuote(true);
    }
  }, [quote, isMovingSlider]);

  const selectedToken = counterPartyTokens.find(entry => entry.token === props.symbol);
  const getAmount = useCallback((): Amount => {
    if (props.counterParty === "taker" && selectedToken?.amount) {
      return selectedToken.amount;
    }
    if (props.symbol && quote) {
      const makerToken = quote.makerTokens[props.symbol];
      if (makerToken) {
        return makerToken.amount ?? Amount.ZERO;
      }
    }
    return Amount.ZERO;
  }, [props.counterParty, props.symbol, quote, selectedToken]);

  const pct = GlobalStore.useState(
    s => s.makerTokens.find(entry => entry.token === props.symbol)?.pct
  );

  const canLock = () => {
    if (props.counterParty === "maker") {
      // We only allow locking if there are more than two unlocked entries remaining
      return (counterPartyTokens as Array<MakerToken>).filter(entry => !entry.isLocked).length > 2;
    }
    return false;
  };

  const getLockedStatus = useCallback(() => {
    if (props.counterParty === "taker") {
      // Locked status is only applicable to maker tokens and is ignored. Returns safely.
      return false;
    }
    const currentToken = (counterPartyTokens as Array<MakerToken>).find(
      entry => entry.token === props.symbol
    );
    return currentToken?.isLocked ?? false;
  }, [counterPartyTokens, props.counterParty, props.symbol]);
  const toggleLockedStatus = () => {
    GlobalStore.update(s => {
      const newTokens = s.makerTokens.map(entry => {
        if (entry.token === props.symbol) {
          return { ...entry, isLocked: !entry.isLocked };
        }
        return entry;
      });
      s.makerTokens = newTokens;
    });
  };

  const getAmountToDisplay = (
    quote: Quote | undefined,
    symbol: Token,
    wrapQuote: WrapQuote | undefined,
    isRequestingQuote: boolean
  ): string => {
    if (!isRequestingQuote) {
      if (quote) {
        return getAmount().toDisplay(symbol, tokens).toString();
      } else if (wrapQuote?.amount) {
        return wrapQuote.amount.toString();
      }
    }
    return Amount.ZERO.toDisplay(symbol, tokens).toString();
  };

  if (!props.symbol) {
    return (
      <div className={`pay-box active symbol-select`} data-testid={"trade-leg"}>
        <div className="row swap-token-row g-0 align-items-center">
          <TokenInputSection
            symbol={props.symbol}
            tokenIcon={getTokenIcon(getWrappedSymbolForChain(network), tokens)}
            tokenSelectMode={props.tokenSelectMode}
            counterParty={props.counterParty}
            isSliderApplicable={isSliderApplicable}
            theme={theme}
          />
        </div>
      </div>
    );
  }
  return (
    <div className={`pay-box active symbol-select`} data-testid={"trade-leg"}>
      <div className="row swap-token-row g-0 align-items-center justify-content-between">
        <div
          css={css`
            display: flex;
            flex-direction: row;
            justify-content: space-between;
          `}
        >
          <div
            css={css`
              width: 100%;
              display: table-cell;
              vertical-align: middle;
              text-align: center;
              margin-right: ${props.counterParty === "taker" && counterPartyTokens.length > 1
                ? "8px"
                : "0"};
            `}
          >
            <TradeRowWrapper>
              <div className={"d-flex justify-content-between"}>
                <TokenInputSection
                  symbol={props.symbol}
                  tokenIcon={getTokenIcon(props.symbol, tokens)}
                  tokenSelectMode={props.tokenSelectMode}
                  counterParty={props.counterParty}
                  isSliderApplicable={isSliderApplicable}
                  theme={theme}
                />
                {isSliderApplicable && (
                  <div
                    className={"col-md-auto col-auto percentage d-flex"}
                    css={css`
                      width: 3.625rem;
                      margin-right: 0.2rem;
                      @media (max-width: 400px) {
                        position: relative;
                        right: 8%;
                      }
                      @media (min-width: 416px) {
                        position: relative;
                        left: 15%;
                      }
                    `}
                  >
                    <LockButton
                      symbol={props.symbol}
                      canLock={canLock()}
                      isVisible={isSliderApplicable && counterPartyTokens.length > 2}
                      onClick={() => {
                        toggleLockedStatus();
                      }}
                      isLocked={getLockedStatus()}
                    />
                    <PercentageLabel
                      theme={theme}
                      symbol={props.symbol}
                      visible={isSliderApplicable}
                    />
                  </div>
                )}
              </div>
              {isRequestingQuote &&
                props.counterParty === "maker" &&
                counterPartyTokens.length === 1 && (
                  <div className="col-sm-2 d-flex justify-content-start px-4 pt-3">
                    <Spinner style={{ marginLeft: 64, marginTop: 16 }} />
                  </div>
                )}
              <div
                className={
                  props.counterParty === "maker"
                    ? "col-md-auto col-auto d-flex align-items-center float-right"
                    : "d-flex"
                }
              >
                <AmountLabel
                  shouldDisplayShimmer={isWaitingForNewQuote}
                  visible={
                    (props.counterParty === "maker" && !!quote && quoteError === undefined) ||
                    (props.counterParty === "maker" && !!wrapQuote?.amount)
                  }
                  amountToDisplay={getAmountToDisplay(
                    quote,
                    props.symbol,
                    wrapQuote,
                    isRequestingQuote
                  )}
                  isSliderApplicable={isSliderApplicable}
                />
                <AmountInput
                  symbol={props.symbol}
                  removeButtonVisible={true}
                  visible={isAmountInputApplicable}
                />
              </div>
            </TradeRowWrapper>
            <BalanceDetails
              shouldDisplayShimmer={isWaitingForNewQuote && props.counterParty === "maker"}
              symbol={props.symbol}
              balanceToDisplay={
                balance?.balance.toDisplay(props.symbol, tokens).toString() ??
                Amount.ZERO.toDisplay(props.symbol, tokens).toString()
              }
              price={price ? price : Amount.ZERO}
              amount={selectedToken ? getAmount() : Amount.ZERO}
              onBalanceClick={() => {
                if (props.counterParty === "taker") {
                  GlobalStore.update(s => {
                    s.takerTokens = s.takerTokens.map(entry => {
                      if (entry.token === props.symbol) {
                        const updatedEntry = cloneDeep(entry);
                        updatedEntry.amount = balance?.balance ?? Amount.ZERO;
                        return updatedEntry;
                      }
                      return entry;
                    });
                  });
                }
              }}
              showBalance={!isSending || props.counterParty === "taker"}
            />
          </div>
          <RemoveButton
            isVisible={counterPartyTokens.length > 1}
            isSliderApplicable={isSliderApplicable}
            onClick={() => {
              const newTokens = counterPartyTokens.filter(entry => entry.token !== props.symbol);
              GlobalStore.update(s => {
                if (props.counterParty === "maker") {
                  // Token locks are not applicable when less than 3 tokens are selected
                  if (newTokens.length < 3) {
                    s.makerTokens = newTokens.map(entry => ({
                      ...entry,
                      isLocked: false,
                    })) as Array<SelectedToken & WithAllocationPct>;
                    return;
                  }
                  s.makerTokens = newTokens as Array<SelectedToken & WithAllocationPct>;
                } else {
                  s.takerTokens = newTokens;
                }
              });
            }}
          />
        </div>
        <Slider
          symbol={props.symbol}
          value={pct ?? 1}
          maxValue={50}
          visible={isSliderApplicable}
          isLocked={getLockedStatus()}
        />
      </div>
    </div>
  );
};
